import { Component, Input, Output, EventEmitter } from '@angular/core';

import { StorageUtilsService } from 'src/app/storage/services/storage-utils.service';
import { LogoImageService } from '../../template-components/services/logo-image.service';
import { FileMetadataUtilsService } from '../../template-components/services/file-metadata-utils.service';

@Component({
  selector: 'template-editor-file-entry',
  templateUrl: './file-entry.component.html',
  styleUrls: ['./file-entry.component.scss'],
  standalone: false,
})
export class FileEntryComponent {
  @Input() fileType: 'image' | 'video';
  @Input() entry: any = {};
  @Output()
  public removeEvent = new EventEmitter<any>();
  @Input() showGripHandle: boolean;

  private readonly STREAMLINE_URI = /^streamline:(.+)/;
  private readonly DEFAULT_URI = 'https://s3.amazonaws.com/Rise-Images/UI/';
  readonly defaultImageThumbnail = LogoImageService.DEFAULT_IMAGE_THUMBNAIL;

  constructor(
    private storageUtilsService: StorageUtilsService,
    private fileMetadataUtilsService: FileMetadataUtilsService,
  ) {}

  getFileName(): string {
    if (!this.entry?.file) {
      return '';
    }

    return this.storageUtilsService.fileNameWithoutExtension(this.entry.file);
  }

  getStoragePath(): string {
    return this.entry
      ? this.storageUtilsService.parseStoragePath(this.entry.file)
      : '';
  }

  getFileTypeLabel(): string {
    return this.entry?.file
      ? this.storageUtilsService.getFileType(this.entry.file)
      : '';
  }

  getThumbnailUrl(): string {
    const thumbnailUrl = this.entry?.['thumbnail-url'];

    return typeof thumbnailUrl === 'string' ? thumbnailUrl : '';
  }

  isStreamlineThumbnail(): boolean {
    const thumbnailUrl = this.getThumbnailUrl();

    return !!(thumbnailUrl && this.STREAMLINE_URI.test(thumbnailUrl));
  }

  isDefaultThumbnail(): boolean {
    const thumbnailUrl = this.getThumbnailUrl();

    return !!(thumbnailUrl && thumbnailUrl.startsWith(this.DEFAULT_URI));
  }

  hasServerThumbnail(): boolean {
    const thumbnailUrl = this.getThumbnailUrl();

    return !!(
      thumbnailUrl &&
      !this.isStreamlineThumbnail() &&
      thumbnailUrl !== LogoImageService.DEFAULT_IMAGE_THUMBNAIL
    );
  }

  shouldUseDefaultImageThumbnail(): boolean {
    return (
      this.fileType === 'image' &&
      this.entry?.exists &&
      !this.hasServerThumbnail() &&
      !this.getImagePreviewUrl()
    );
  }

  getImagePreviewUrl(): string {
    if (
      this.fileType !== 'image' ||
      !this.entry?.exists ||
      this.hasServerThumbnail() ||
      this.entry?.previewFailed
    ) {
      return '';
    }

    return this.entry?.['media-link'] || '';
  }

  onImagePreviewError(): void {
    this.entry.previewFailed = true;
  }

  getVideoPreviewUrl(): string {
    if (this.fileType !== 'video' || !this.isStreamlineThumbnail()) {
      return '';
    }

    return this.fileMetadataUtilsService.videoPosterUrlFor(
      this.entry?.['media-link'],
    );
  }

  removeFileFromList(): void {
    this.removeEvent.emit(this.entry);
  }
}
